import * as React from 'react';
import * as _ from 'lodash';
import {
  Logging,
  ControlState,
  ControlStyleClass,
  ControlValidation,
  Coverage,
  stringToDate,
  formatDate,
  DateFormatEnum,
  renderDate,
  renderEdit,
  Jurisdiction,
  renderFieldValidation,
  certificateUuidMarker,
} from '@vcd-ecm-ui/Common';
import { TreeSelect, TreeSelectData } from '@vcd-ecm-ui/GenericGrid';
import CoverageItemProps from './CoverageItemProps.interface';
import '../steps.scss';

// Default Style
const defaultControlStyle: ControlStyleClass = {
  fieldHeader: 'defaultFieldHeader',
  fieldBase: 'defaultFieldBase',
  fieldEnabled: 'defaultFieldEnabled',
  fieldDisabled: 'defaultFieldDisabled',
  fieldValidation: 'defaultFieldValidation',
  fieldDropDown: null,
};

const controlStateEditable: ControlState = {
  editable: true,
  disabled: false,
  readOnly: false,
};

const controlStyle: ControlStyleClass = null;

const controlValidation: ControlValidation = {
  requiredFields: ['jurisdiction'],
  customMessages: null,
};

const CoverageItem = ({
  coverage,
  coverages,
  certificate,
  jurisdictions,
  exemptionReason,
  onUpdateCoverage,
}: CoverageItemProps): JSX.Element => {
  // Format the Jurisdiction Id and name | delimited.  The Tree Select search on the value
  const formatJurisdictionIdName = (jurisdiction: Jurisdiction): string => { // eslint-disable-line arrow-body-style
    return jurisdiction ? `${jurisdiction.id}|${jurisdiction.name}` : null;
  };

  // Extract the Id from the Id | Name
  const getJurisdictionId = (jurisdiction: string): number => {
    const items: string[] = jurisdiction.split('|');

    return Number(items[0]);
  };

  const key = coverage.uuid;

  Logging.Debug('Coverage', key, coverage);

  const [treeData, setTreeData] = React.useState(null);

  const [jurisdictionIdName, setJurisdictionIdName] = React.useState(formatJurisdictionIdName(coverage?.jurisdiction));
  const [issueDate, setIssueDate] = React.useState(stringToDate(coverage?.issueDate));
  const [reviewDate, setReviewDate] = React.useState(stringToDate(coverage?.reviewDate));
  const [expirationDate, setExpirationDate] = React.useState(stringToDate(coverage?.expirationDate));

  // Update the Coverage Values
  const updateValues = (): Coverage => {
    // Get the Selected Jurisdiction
    const jurisdiction: Jurisdiction = jurisdictionIdName
      ? jurisdictions.filter((jurisdictionObject: Jurisdiction) => jurisdictionObject.id === getJurisdictionId(jurisdictionIdName))[0]
      : null;

    const updatedCoverage: Coverage = {
      uuid: coverage.uuid,
      jurisdiction,
      issueDate: issueDate ? formatDate(issueDate, { dateFormat: DateFormatEnum.YMD, dateSeperator: '-' }) : null,
      reviewDate: reviewDate ? formatDate(reviewDate, { dateFormat: DateFormatEnum.YMD, dateSeperator: '-' }) : null,
      expirationDate: expirationDate ? formatDate(expirationDate, { dateFormat: DateFormatEnum.YMD, dateSeperator: '-' }) : null,
      certificateNumber: null, // FIX ME: What should this be?
      reason: exemptionReason,
      impositions: null, // FIX ME: What should this be?
    };

    return updatedCoverage;
  };

  // Jurisdiction Sorter Function - Sort alphabetically by name
  const compareJurisdictions = (a: Jurisdiction, b: Jurisdiction): number => {
    // Use toUpperCase() to ignore character casing
    const nameA = a.name.toUpperCase();
    const nameB = b.name.toUpperCase();

    let comparison = 0;

    if (nameA > nameB) {
      comparison = 1;
    } else if (nameA < nameB) {
      comparison = -1;
    }

    return comparison;
  };

  // Get the Tree Data for the Jurisdiction
  const getJurisdictionData = (jurisdictionObject: Jurisdiction): TreeSelectData => {
    // Get the Children for the Jurisdiction
    const children: Jurisdiction[] = jurisdictions.filter((childJurisdiction: Jurisdiction) => (childJurisdiction.parentId === jurisdictionObject.id)); // eslint-disable-line max-len

    if (children.length) {
      Logging.Debug('Parent:', jurisdictionObject.name, children.length);
    }

    const jurisdictionTreeData: TreeSelectData = {
      title: jurisdictionObject.name,
      value: formatJurisdictionIdName(jurisdictionObject), // Use Id and Name to prevent Duplicates
      // Loop through the Children of the Jurisdiction
      children: children?.sort(compareJurisdictions).map((childJurisdiction: Jurisdiction) => {
        Logging.Debug('Child:', childJurisdiction.name);
        return getJurisdictionData(childJurisdiction);
      }),
    };

    return jurisdictionTreeData;
  };

  // Render a Tree Select
  const renderTreeSelect = (
    size: number,
    id: string,
    heading: string,
    placeholder: string,
    value: number | string,
    onChangeValue: React.Dispatch<React.SetStateAction<number | string>>,
    state: ControlState,
    style: ControlStyleClass,
    validation: ControlValidation = null,
  ): JSX.Element => {
    const className = `field${size}`;
    const styleCombined: ControlStyleClass = style || defaultControlStyle;
    const classEnableDisable = state.editable ? styleCombined.fieldEnabled : styleCombined.fieldDisabled;

    return (
      <div className={className}>
        <div className={`form-item ${styleCombined.fieldBase} ${classEnableDisable}`}>
          {
            heading
            && (
              <label className={styleCombined.fieldHeader} htmlFor={id}>{heading}</label>
            )
          }
          <TreeSelect
            treeData={treeData}
            value={value}
            onChange={(newValue: number | string, label: string[]) => onChangeValue(newValue)}
            placeholder={placeholder}
            showSearch
          />
          {renderFieldValidation(id, `${value || ''}`, state, validation, styleCombined)}
        </div>
      </div>
    );
  };

  // Determine if Issue / Review Date must be enabled
  const disabledIssueReviewDate = (date: Date): boolean => {
    if (certificate.effectiveDate) {
      const certificateStartDate = stringToDate(certificate.effectiveDate);

      if (date < certificateStartDate) {
        return true;
      }
    }

    if (certificate.endDate) {
      const certificateEndDate = stringToDate(certificate.endDate);

      if (date > certificateEndDate) {
        return true;
      }
    }

    return false;
  };

  // Determine if Expiration Date must be enabled
  const disabledExpirationDate = (date: Date): boolean => {
    if (certificate.effectiveDate) {
      const certificateStartDate = stringToDate(certificate.effectiveDate);

      if (date < certificateStartDate) {
        return true;
      }
    }

    return false;
  };

  // Set the Tree Select Data
  React.useEffect(() => {
    if (jurisdictions) {
      Logging.Debug('Jurisdictions:', jurisdictions);

      // Loop through the Root Jurisdictions
      const data: TreeSelectData[] = jurisdictions.filter((jurisdictionObject: Jurisdiction) => (jurisdictionObject.parentId === null)).sort(compareJurisdictions).map((rootJurisdiction: Jurisdiction) => { // eslint-disable-line max-len
        Logging.Debug('Root Jurisdiction:', rootJurisdiction.name);

        return getJurisdictionData(rootJurisdiction);
      });

      setTreeData(data);
    }
  }, []);

  // eslint-disable-next-line arrow-body-style
  React.useEffect(() => {
    const updatedCoverage: Coverage = updateValues();

    // Check to see if the Original Coverage was modified
    if (!_.isEqual(coverage, updatedCoverage)) {
      Logging.Debug('Not Equal', key, coverage, updatedCoverage);
      onUpdateCoverage(updatedCoverage);
    }
  });

  // Clear the Custom Messages
  controlValidation.customMessages = [];

  // Only Validate once Jurisdiction Selected
  if (jurisdictionIdName) {
    const instances = coverages.filter((coverageItem: Coverage) => coverageItem.jurisdiction?.id === getJurisdictionId(jurisdictionIdName)).length;

    // Check if it is unique
    if (instances > 1) {
      controlValidation.customMessages = [...controlValidation.customMessages,
        // eslint-disable-next-line max-len
        { field: 'jurisdiction', message: 'The Jurisdiction must be unique.', type: 'Error' }];
    }

    // Validate against Certificate Start Date
    if (certificate && certificate.effectiveDate) {
      const certificateStartDate = stringToDate(certificate.effectiveDate);

      Logging.Debug('Certificate Start Date:', certificateStartDate, issueDate, expirationDate);

      if (issueDate && (issueDate < certificateStartDate)) {
        controlValidation.customMessages = [...controlValidation.customMessages,
          // eslint-disable-next-line max-len
          { field: 'issueDate', message: `Issue cannot be before Certificate Start (${certificate.effectiveDate})`, type: 'Error' }];
      }

      if (reviewDate && (reviewDate < certificateStartDate)) {
        controlValidation.customMessages = [...controlValidation.customMessages,
          // eslint-disable-next-line max-len
          { field: 'reviewDate', message: `Review cannot be before Certificate Start (${certificate.effectiveDate})`, type: 'Error' }];
      }

      if (expirationDate && (expirationDate < certificateStartDate)) {
        controlValidation.customMessages = [...controlValidation.customMessages,
          // eslint-disable-next-line max-len
          { field: 'expirationDate', message: `Expiration cannot be before Certificate Start (${certificate.effectiveDate})`, type: 'Error' }];
      }
    }

    // Validate against Certificate End Date
    if (certificate && certificate.endDate) {
      const certificateEndDate = stringToDate(certificate.endDate);

      Logging.Debug('Certificate End Date:', certificateEndDate, issueDate);

      if (issueDate && (issueDate > certificateEndDate)) {
        controlValidation.customMessages = [...controlValidation.customMessages,
          // eslint-disable-next-line max-len
          { field: 'issueDate', message: `Issue cannot be after Certificate End (${certificate.endDate})`, type: 'Error' }];
      }

      if (reviewDate && (reviewDate > certificateEndDate)) {
        controlValidation.customMessages = [...controlValidation.customMessages,
          // eslint-disable-next-line max-len
          { field: 'reviewDate', message: `Review cannot be after Certificate End (${certificate.endDate})`, type: 'Error' }];
      }
    }

    if (issueDate && reviewDate && (reviewDate < issueDate)) {
      controlValidation.customMessages = [...controlValidation.customMessages,
        // eslint-disable-next-line max-len
        { field: 'reviewDate', message: 'Review Date cannot be before Issue Date', type: 'Error' }];
    }
  }

  return (
    <div className="coverageContainer">
      {renderTreeSelect(50, 'jurisdiction', 'Jurisdiction', 'Select a Jurisdiction', jurisdictionIdName, setJurisdictionIdName, controlStateEditable,
        controlStyle, controlValidation)}
      {renderDate(50, 'issueDate', 'Issue Date', 'Select an Issue Date', issueDate, setIssueDate, disabledIssueReviewDate, controlStateEditable,
        controlStyle, controlValidation)}
      {renderDate(50, 'reviewDate', 'Review Date', 'Select a Review Date', reviewDate, setReviewDate, disabledIssueReviewDate, controlStateEditable,
        controlStyle, controlValidation)}
      {renderDate(50, 'expirationDate', 'Expiration Date', 'Select an Expiration Date', expirationDate, setExpirationDate, disabledExpirationDate,
        controlStateEditable, controlStyle, controlValidation)}
    </div>
  );
};

export default CoverageItem;
